(() => {
  const SELECTOR = '[data-dp-botanical-collections]';

  const escapeHTML = (value = '') =>
    String(value).replace(/[&<>"']/g, (char) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    }[char]));

  const normalize = (value = '') =>
    String(value)
      .toLocaleLowerCase('bs')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

  const stripHTML = (value = '') => {
    const template = document.createElement('template');
    template.innerHTML = String(value);
    return (template.content.textContent || '').replace(/\s+/g, ' ').trim();
  };

  class DPProductFamilies {
    constructor(section) {
      this.section = section;
      this.track = section.querySelector('[data-dp-track]');
      this.tabs = Array.from(section.querySelectorAll('[data-dp-tab]'));
      this.prev = section.querySelector('[data-dp-prev]');
      this.next = section.querySelector('[data-dp-next]');
      this.progress = section.querySelector('[data-dp-progress]');
      this.progressLeaf = section.querySelector('[data-dp-progress-leaf]');
      this.current = section.querySelector('[data-dp-current]');
      this.total = section.querySelector('[data-dp-total]');
      this.status = section.querySelector('[data-dp-status]');
      this.limit = Math.max(2, Number(section.dataset.productsLimit || 6));
      this.currency = section.dataset.currency || 'BAM';
      this.locale = section.dataset.moneyLocale || 'bs-BA';
      this.activeCategory = 'featured';
      this.activeIndex = 0;
      this.cards = [];
      this.scrollFrame = null;
      this.cache = new Map();

      const json = section.querySelector('[data-dp-products-json]');
      try {
        this.products = JSON.parse(json?.textContent || '[]');
      } catch (_) {
        this.products = [];
      }

      this.bind();
      this.prepareTabs();
      this.refreshCards();
      this.setActive(0, false);
    }

    context(product) {
      return normalize([
        product.title,
        product.handle,
        product.productType,
        product.templateSuffix,
        Array.isArray(product.tags) ? product.tags.join(' ') : product.tags,
        product.context
      ].filter(Boolean).join(' '));
    }

    matches(product, category) {
      const context = this.context(product);
      const template = normalize(product.templateSuffix || '');

      const isPet = /(pet|pets|ljubim|pas\b|psi\b|macka|macak|dog\b|cat\b|animal|veterinar)/.test(context);
      const isTeaOrPropolis = /(caj|tea|propolis|med\b|honey|biljni caj|herbal)/.test(context);
      const isCBD = template.includes('cbd') || /(^|\s)cbd(\s|$)|kanabidiol/.test(context);
      const isSupplement =
        template.includes('supplement') ||
        /(ashwagandha|asvaganda|ginko|ginkgo|ginseng|berberin|cimet|cinnamon|spirulina|maka|maca|vitamin|mineral|complex|kompleks|shiitake|bijeli luk|beli luk|crni kim|corokot|selen|magnezij|zinc|cink)/.test(context);

      if (category === 'featured') {
        return /(najtrazen|najprodavan|bestseller|best seller|featured|izdvojeno|popularno)/.test(context);
      }

      if (category === 'pets') return isPet;
      if (category === 'tea-propolis') return !isPet && isTeaOrPropolis;
      if (category === 'cbd') return !isPet && isCBD;
      if (category === 'supplements') return !isPet && !isCBD && !isTeaOrPropolis && isSupplement;

      return false;
    }

    productsFor(category) {
      let products = this.products.filter((product) => this.matches(product, category));

      if (category === 'featured' && !products.length) {
        products = [...this.products];
      }

      return products
        .sort((a, b) => Number(b.available) - Number(a.available))
        .slice(0, this.limit);
    }

    prepareTabs() {
      this.tabs.forEach((tab) => {
        const category = tab.dataset.category;
        const products = this.productsFor(category);
        tab.hidden = !products.length;
      });

      const firstVisible = this.tabs.find((tab) => !tab.hidden);
      if (!firstVisible) return;

      this.tabs.forEach((tab) => {
        const active = tab === firstVisible;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
      });

      this.activeCategory = firstVisible.dataset.category;
      this.renderCategory(
        this.activeCategory,
        firstVisible.dataset.categoryLabel || firstVisible.textContent.trim()
      );
    }

    bind() {
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => {
          if (tab.hidden || tab.dataset.category === this.activeCategory) return;

          this.tabs.forEach((item) => {
            const active = item === tab;
            item.classList.toggle('is-active', active);
            item.setAttribute('aria-selected', active ? 'true' : 'false');
          });

          this.activeCategory = tab.dataset.category;
          this.renderCategory(
            this.activeCategory,
            tab.dataset.categoryLabel || tab.textContent.trim()
          );
        });
      });

      this.prev?.addEventListener('click', () => this.setActive(this.activeIndex - 1, true));
      this.next?.addEventListener('click', () => this.setActive(this.activeIndex + 1, true));

      this.track?.addEventListener('scroll', () => {
        if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = requestAnimationFrame(() => this.detectActive());
      }, { passive: true });

      this.track?.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.setActive(this.activeIndex + 1, true);
        }

        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.setActive(this.activeIndex - 1, true);
        }
      });

      this.section.addEventListener('click', (event) => {
        const button = event.target.closest('[data-dp-quick-add]');
        if (button) this.quickAdd(button);
      });
    }

    formatMoney(cents) {
      const value = Number(cents || 0) / 100;

      try {
        return new Intl.NumberFormat(this.locale, {
          style: 'currency',
          currency: this.currency,
          minimumFractionDigits: 2
        }).format(value);
      } catch (_) {
        return `${value.toFixed(2).replace('.', ',')} KM`;
      }
    }

    cardHTML(product, index, label) {
      const title = escapeHTML(product.title || 'Proizvod');
      const url = escapeHTML(product.url || `/products/${product.handle}`);
      const image = escapeHTML(product.image || '');
      const alt = escapeHTML(product.imageAlt || product.title || '');
      const description = escapeHTML(
        stripHTML(product.description || 'Pogledajte detalje, sastav i način upotrebe proizvoda.')
          .slice(0, 112)
      );
      const available = Boolean(product.available);
      const compareAt = Number(product.compareAtPrice || 0);
      const price = Number(product.price || 0);

      return `
        <article class="dpbc-card${index === 0 ? ' is-active' : ''}" data-dp-card data-index="${index}" data-product-title="${title}" role="listitem">
          <div class="dpbc-card__visual">
            <span class="dpbc-card__halo" aria-hidden="true"></span>
            <span class="dpbc-card__orbit" aria-hidden="true"></span>
            <span class="dpbc-card__number" aria-hidden="true">${String(index + 1).padStart(2, '0')}</span>
            <span class="dpbc-card__badge">
              <span class="dpbc-card__badge-dot" aria-hidden="true"></span>
              ${index === 0 ? 'Izdvojeno' : 'Pažljivo odabrano'}
            </span>

            <a class="dpbc-card__image-link" href="${url}" aria-label="${title}">
              ${image
                ? `<img class="dpbc-card__image" src="${image}" alt="${alt}" loading="lazy" width="700" height="700">`
                : '<div class="dpbc-card__placeholder"></div>'}
            </a>
            <span class="dpbc-card__ground" aria-hidden="true"></span>
          </div>

          <div class="dpbc-card__content">
            <p class="dpbc-card__category">${escapeHTML(label)}</p>
            <h3 class="dpbc-card__title"><a href="${url}">${title}</a></h3>
            <p class="dpbc-card__benefit">${description}</p>

            <div class="dpbc-card__purchase-row">
              <div class="dpbc-card__prices">
                <span class="dpbc-card__price">${this.formatMoney(price)}</span>
                ${compareAt > price ? `<span class="dpbc-card__compare">${this.formatMoney(compareAt)}</span>` : ''}
              </div>
              <a class="dpbc-card__details" href="${url}">
                <span>Detalji</span>
                <svg viewBox="0 0 18 18" fill="none" aria-hidden="true">
                  <path d="M4.4 13.6L13.6 4.4M7.2 4.4H13.6V10.8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </a>
            </div>

            <button
              class="dpbc-card__button"
              type="button"
              data-dp-quick-add
              data-variant-id="${Number(product.variantId || 0)}"
              data-quantity="1"
              ${available ? '' : 'disabled'}
            >
              <span class="dpbc-card__button-cart" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M3.5 5.5H6L7.5 14.1C7.7 15.2 8.4 15.8 9.5 15.8H17.2C18.2 15.8 18.9 15.3 19.1 14.3L20.2 8.6C20.4 7.5 19.8 6.8 18.7 6.8H7.1" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                  <circle cx="9.7" cy="19" r="1.3" fill="currentColor"/>
                  <circle cx="17" cy="19" r="1.3" fill="currentColor"/>
                </svg>
              </span>
              <span data-dp-button-label>${available ? 'Dodaj u korpu' : 'Rasprodano'}</span>
              <span class="dpbc-card__button-arrow" aria-hidden="true">→</span>
            </button>
          </div>
        </article>
      `;
    }

    renderCategory(category, label) {
      const products = this.productsFor(category);

      if (!products.length) {
        this.track.innerHTML = '<div class="dpbc__empty">U ovoj grupi trenutno nema proizvoda.</div>';
        this.status.textContent = '';
      } else {
        const cacheKey = `${category}:${label}`;
        let html = this.cache.get(cacheKey);

        if (!html) {
          html = products.map((product, index) => this.cardHTML(product, index, label)).join('');
          this.cache.set(cacheKey, html);
        }

        this.track.innerHTML = html;
        this.track.setAttribute('aria-label', label);
        this.track.scrollLeft = 0;
        this.status.textContent = `${label}: prikazano ${products.length} proizvoda.`;
      }

      this.refreshCards();
      this.setActive(0, false);
    }

    refreshCards() {
      this.cards = this.track ? Array.from(this.track.querySelectorAll('[data-dp-card]')) : [];
      this.activeIndex = 0;
      this.updateNavigation();
    }

    detectActive() {
      if (!this.track || !this.cards.length || matchMedia('(min-width: 990px)').matches) return;

      const left = this.track.getBoundingClientRect().left;
      let nearest = 0;
      let distance = Infinity;

      this.cards.forEach((card, index) => {
        const nextDistance = Math.abs(card.getBoundingClientRect().left - left);
        if (nextDistance < distance) {
          distance = nextDistance;
          nearest = index;
        }
      });

      if (nearest !== this.activeIndex) this.setActive(nearest, false);
    }

    setActive(index, scroll) {
      if (!this.cards.length) {
        this.updateNavigation();
        return;
      }

      this.activeIndex = Math.max(0, Math.min(index, this.cards.length - 1));

      this.cards.forEach((card, cardIndex) => {
        card.classList.toggle('is-active', cardIndex === this.activeIndex);
      });

      if (scroll) {
        this.cards[this.activeIndex]?.scrollIntoView({
          behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          block: 'nearest',
          inline: 'start'
        });
      }

      this.updateNavigation();
    }

    updateNavigation() {
      const count = this.cards.length;
      const percent = count <= 1 ? (count ? 100 : 0) : (this.activeIndex / (count - 1)) * 100;

      if (this.progress) this.progress.style.width = `${percent}%`;
      if (this.progressLeaf) this.progressLeaf.style.left = `${percent}%`;
      if (this.current) this.current.textContent = count ? String(this.activeIndex + 1).padStart(2, '0') : '00';
      if (this.total) this.total.textContent = String(count).padStart(2, '0');
      if (this.prev) this.prev.disabled = !count || this.activeIndex === 0;
      if (this.next) this.next.disabled = !count || this.activeIndex === count - 1;
    }

    async quickAdd(button) {
      if (button.disabled || button.dataset.loading === 'true') return;

      const variantId = Number(button.dataset.variantId);
      const quantity = Math.max(1, Number(button.dataset.quantity || 1));
      const label = button.querySelector('[data-dp-button-label]');
      const original = label?.textContent || 'Dodaj u korpu';

      if (!variantId) return;

      button.dataset.loading = 'true';
      button.disabled = true;
      if (label) label.textContent = 'Dodajem…';

      try {
        if (window.DarPrirodeCart?.addItems) {
          await window.DarPrirodeCart.addItems([{ id: variantId, quantity }], 'product-families');
          window.DarPrirodeCart.openDrawer?.();
        } else {
          const root = window.Shopify?.routes?.root || '/';
          const response = await fetch(`${root}cart/add.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json'
            },
            body: JSON.stringify({ items: [{ id: variantId, quantity }] })
          });

          if (!response.ok) throw new Error('Dodavanje nije uspjelo.');
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        }

        if (label) label.textContent = 'Dodano ✓';

        setTimeout(() => {
          button.disabled = false;
          button.dataset.loading = 'false';
          if (label) label.textContent = original;
        }, 1200);
      } catch (_) {
        if (label) label.textContent = 'Pokušaj ponovo';

        setTimeout(() => {
          button.disabled = false;
          button.dataset.loading = 'false';
          if (label) label.textContent = original;
        }, 1700);
      }
    }
  }

  const init = (root = document) => {
    if (root.matches?.(SELECTOR) && root.dataset.dpbcInitialized !== 'true') {
      root.dataset.dpbcInitialized = 'true';
      new DPProductFamilies(root);
    }

    root.querySelectorAll?.(SELECTOR).forEach((section) => {
      if (section.dataset.dpbcInitialized === 'true') return;
      section.dataset.dpbcInitialized = 'true';
      new DPProductFamilies(section);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
